import { useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useSymptomSearch,
  useCreatePatientSymptom,
  usePatientSymptoms,
  useDeletePatientSymptom,
} from "@/hooks/useConsultation";
import { useDebounce } from "@/hooks/use-debounce";
import { SearchableChipDialog } from "@/components/consultation/shared/SearchableChipDialog";
import { groupSymptomsByType } from "@/lib/symptoms";
import type { Symptom } from "@/types/appointments";

interface AddSymptomsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
}

export const AddSymptomsDialog = ({
  open,
  onOpenChange,
  appointmentId,
}: AddSymptomsDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { toast } = useToast();

  const { data: searchResults = [], isLoading: isSearching } =
    useSymptomSearch(debouncedSearchTerm);
  const { data: existingSymptoms = [] } = usePatientSymptoms(appointmentId);
  const createSymptom = useCreatePatientSymptom();
  const deleteSymptom = useDeletePatientSymptom();

  // Inactive draft items must be hidden in the appointment UI. A previously
  // deactivated symptom should be re-addable from search; the backend will
  // reactivate the same draft row in that case.
  const activeExistingSymptoms = useMemo(
    () => existingSymptoms.filter((s) => s.activity_status === "active"),
    [existingSymptoms]
  );

  // PatientSymptom.symptoms is null for free-text custom rows. Only catalog
  // rows can collide with search results, so build the dedupe set from those.
  const addedSymptomIds = new Set(
    activeExistingSymptoms
      .map((s) => s.symptoms?.id)
      .filter((id): id is string => Boolean(id))
  );

  const filteredResults = searchResults.filter(
    (s: Symptom) => !addedSymptomIds.has(s.id)
  );

  // Mirror the grouped layout used by the symptom cards so the dialog feels
  // consistent. Empty buckets are dropped to keep the surface tight.
  const addedGroups = useMemo(
    () =>
      groupSymptomsByType(activeExistingSymptoms)
        .filter((g) => g.items.length > 0)
        .map((g) => ({
          key: g.key,
          label: g.label,
          items: g.items.map((s) => ({
            id: s.id,
            label: s.symptoms?.name ?? s.custom_symptoms ?? "Custom symptom",
          })),
        })),
    [activeExistingSymptoms]
  );

  const handleAddSymptom = async (id: string) => {
    const symptom = searchResults.find((s: Symptom) => s.id === id);
    if (!symptom) return;
    try {
      await createSymptom.mutateAsync({
        appointment: appointmentId,
        symptoms: symptom.id,
        status: "Ongoing-increased",
      });
      toast({ title: "Success", description: `"${symptom.name}" added` });
      setSearchTerm("");
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to add symptom",
        variant: "destructive",
      });
    }
  };

  const handleRemoveSymptom = async (id: string) => {
    try {
      await deleteSymptom.mutateAsync({
        symptomId: id,
        appointmentId,
      });
      toast({ title: "Success", description: "Symptom removed" });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to remove symptom",
        variant: "destructive",
      });
    }
  };

  return (
    <SearchableChipDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Symptoms"
      addedGroups={addedGroups}
      onRemoveItem={handleRemoveSymptom}
      searchLabel="Add symptoms here"
      searchPlaceholder="Search Here"
      searchResults={filteredResults.map((s: Symptom) => ({
        id: s.id,
        label: s.name,
      }))}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      minSearchChars={2}
      isSearching={isSearching}
      emptySearchMessage="No matching symptoms found"
      onAddItem={handleAddSymptom}
    />
  );
};
